import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "core/lib/utils";
import { secureRandom } from "core/utils/secureRandom";
import {
  AnimatePresenceContainer,
  EditableTextBox,
  InputContainer,
} from "./ImageVanishInput.styled";
import { useAppTheme } from "core/contexts/theme/theme";
import { FilePreviewCard } from "./FilePreviewCard";
import VoiceChat from "core/components/global/input/voice/voiceChat";
import { ALLOWED_FILE_TYPES, MAX_FILE_SIZE } from "../../../../utils/filetype"

type Props = {
  onExcelUpload: (file: File) => void;
};

export function PlaceholdersAndVanishInput({
  placeholders,
  handlePromptSubmit,
  isWaitingForResponse,
  isWide,
  suggestedPrompt,
  acceptsText,
  acceptsFiles,
  onExcelUpload,
  providerName
}) {
  const [currentPlaceholder, setCurrentPlaceholder] = useState(0);
  const [currentPlaceholderText, setCurrentPlaceholderText] = useState("");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const newDataRef = useRef<any[]>([]);
  const inputRef = useRef<any>(null);
  const [animating, setAnimating] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [userPrompt, setUserPrompt] = useState<string>(suggestedPrompt || "");
  const contentEditableRef = useRef<HTMLSpanElement | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const MAX_CHARACTERS = 5000

  useEffect(() => {
    if (contentEditableRef.current) {
      (contentEditableRef.current as HTMLSpanElement).innerText = "";
    }

    console.log(`providerName: ${providerName}`);
    console.log(`acceptsFiles: ${acceptsFiles}`);
    console.log(`acceptsText: ${acceptsText}`);
  }, [providerName])

  useEffect(() => {
    if (contentEditableRef.current && suggestedPrompt) {
      contentEditableRef.current.innerText = suggestedPrompt;
      setUserPrompt(suggestedPrompt);
    }
  }, [suggestedPrompt]);

  const triggerFileInput = (e) => {
    e.preventDefault();
    const fileInput = document.getElementById("file-upload");
    if (fileInput) {
      fileInput.click();
    } else {
      console.error("File input element not found!");
    }
  };

  const drawTextVanishAnimation = useCallback(() => {
    if (!inputRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = 200;
    canvas.height = 200;
    ctx.clearRect(0, 0, 200, 200);
    const computedStyles = getComputedStyle(inputRef.current);

    const fontSize = parseFloat(computedStyles.getPropertyValue("font-size"));
    ctx.font = `${fontSize * 2}px ${computedStyles.fontFamily}`;
    ctx.fillStyle = "#FFF";
    ctx.fillText(userPrompt, 16, 40);

    const imageData = ctx.getImageData(0, 0, 200, 200);
    const pixelData = imageData.data;
    const newData: any[] = [];

    for (let t = 0; t < 200; t++) {
      let i = 4 * t * 200;
      for (let n = 0; n < 200; n++) {
        let e = i + 4 * n;
        if (
          pixelData[e] !== 0 &&
          pixelData[e + 1] !== 0 &&
          pixelData[e + 2] !== 0
        ) {
          newData.push({
            x: n,
            y: t,
            color: [
              pixelData[e],
              pixelData[e + 1],
              pixelData[e + 2],
              pixelData[e + 3],
            ],
          });
        }
      }
    }

    newDataRef.current = newData.map(({ x, y, color }) => ({
      x,
      y,
      r: 1,
      color: `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${color[3]})`,
    }));

    drawTextVanishAnimation();
  }, [userPrompt]);

  const animate = (start: number) => {
    const animateFrame = (pos: number = 0) => {
      requestAnimationFrame(() => {
        const newArr: any = [];
        for (let i = 0; i < newDataRef.current.length; i++) {
          const current = newDataRef.current[i];
          if (current.x < pos) {
            newArr.push(current);
          } else {
            if (current.r <= 0) {
              current.r = 0;
              continue;
            }
            current.x += secureRandom() > 0.5 ? 1 : -1;
            current.y += secureRandom() > 0.5 ? 1 : -1;
            current.r -= 0.05 * secureRandom();
            newArr.push(current);
          }
        }
        newDataRef.current = newArr;
        const ctx = canvasRef.current?.getContext("2d");
        if (ctx) {
          ctx.clearRect(pos, 0, 200, 200);
          newDataRef.current.forEach((t) => {
            const { x: n, y: i, r: s, color: color } = t;
            if (n > pos) {
              ctx.beginPath();
              ctx.rect(n, i, s, s);
              ctx.fillStyle = color;
              ctx.strokeStyle = color;
              ctx.stroke();
            }
          });
        }
        if (newDataRef.current.length > 0) {
          animateFrame(pos - 8);
        } else {
          setUserPrompt("");
          setAnimating(false);
        }
      });
    };
    animateFrame(start);
  };

  const vanishText = (e) => {
    if (e) {
      e.preventDefault();
    }
    setAnimating(true);
    drawTextVanishAnimation();

    const value = inputRef.current?.value || "";
    if (value && inputRef.current) {
      const maxX = newDataRef.current.reduce(
        (prev, current) => (current.x > prev ? current.x : prev),
        0
      );
      animate(maxX);
    }
  };

  

  const handleSubmitInput = async (e) => {
    e.preventDefault();
    // Vanish the text w. animation
    vanishText(null);

    // Send the user input saved via 'handleInputChange' to the backend
    if (userPrompt.trim() !== "") {
      handlePromptSubmit(userPrompt);
    } 
    // Else if a preview file exists (pending image/file input), send it instead
    else if (previewFile) {
      // 1. Get signed URL from backend
      
      handlePromptSubmit(previewFile);
      setPreviewFile(null); // clear after sending
    }

    if (contentEditableRef.current) {
      (contentEditableRef.current as HTMLSpanElement).innerText = "";
    }
    setUserPrompt("");
    // Userprompt overflow is reset
    setIsOverflowing(false);
  };

  const handleNewCharacter = (e) => {
    // Update the textarea height with a max height + scroll
    if (e.target.scrollHeight > 300) {
      /// Max H + S
      setIsOverflowing(true);
    } else {
      /// Below max h, expansion is tolerated
      setIsOverflowing(false);
    }
  };

  /**
   * Manage OnChange (new text) and OnKeyDown (Enter btn)
   * @param e
   */
  const handleTextAreaActions = (e) => {
    const text = e.currentTarget.innerText;
    setUserPrompt(e.currentTarget.innerText);
    if (text.length > MAX_CHARACTERS) {
    // Trim the text back to max limit
    e.currentTarget.innerText = text.slice(0, MAX_CHARACTERS);
    // Move cursor to end after trimming
    const range = document.createRange();
    const sel = window.getSelection();
    range.selectNodeContents(e.currentTarget);
    range.collapse(false);
    sel?.removeAllRanges();
    sel?.addRange(range);
  }

  setUserPrompt(e.currentTarget.innerText);

    // Shift + Enter → New Line
    if (e.key === "Enter" && e.shiftKey) {
      handleNewCharacter(e);
      return;
    }
  
    // Regular key press (not Enter) → Animate character
    if (e.key !== "Enter") {
      handleNewCharacter(e);
      return;
    }
  
    // Enter key
    if (e.key === "Enter") {
      // Don't submit if there's a file selected
      if (previewFile) {
        e.preventDefault();
        return; // Prevent Enter submission for file/image
      }
  
      // Submit if it's text input
      handleSubmitInput(e);
    }
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const extension = file.name.split(".").pop()?.toLowerCase();
    const mimeType = file.type;

     // Extension check
  if (!extension || !ALLOWED_FILE_TYPES[extension]) {
    alert(`Invalid file type ${extension}. Allowed File Formats are: ${Object.keys(ALLOWED_FILE_TYPES).join(", ")}`);
    e.target.value = "";
    return;
  }
   
  // MIME check
  if (!ALLOWED_FILE_TYPES[extension].includes(mimeType)) {
    alert(`File type mismatch. Expected ${ALLOWED_FILE_TYPES[extension].join(", ")} but got ${mimeType}`);
    e.target.value = "";
    return;
  }
    // File size check
  if (file.size > MAX_FILE_SIZE) {
    alert("File size exceeds 5MB limit.");
    e.target.value = "";
    return;
  }

    if (extension === "xlsx" || extension === "xls") {
      onExcelUpload(file); // ✅ send file to the main uploader
    }

    // Optional: Handle image/file preview separately
    setPreviewFile(file);
    e.target.value = "";
  };
  
  /**
   * -- For the text revolution animation --
   * Each 3 seconds loop through the next item in a loop, using the remainder
   */
  const startAnimation = () => {
    intervalRef.current = setInterval(() => {
      setCurrentPlaceholder((prev) => (prev + 1) % placeholders.length);
    }, 3000);
  };

  /**
   * -- For the text revolution animation --
   * When/If placeholders change because of an IAU/Provider change, clear and restart the animation
   */
  useEffect(() => {
    // If the animation reference is running, clear it
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    } 

    // If there's no text, start the animation
    if (userPrompt.toString().trim() === "") {
      setCurrentPlaceholder(0)
      startAnimation();
    }
  }, [placeholders]);

  /**
   * -- For the text revolution animation --
   * When the placeholders change because of an IAU/Provider change, clear and restart the animation
   * or when the currentPlaceholder index changes, update the text in the placeholder animation
   */
  useEffect(() => {
    if(placeholders.length > 0) {
      if (typeof currentPlaceholder !== "number") {
        setCurrentPlaceholderText(placeholders[0]?.prompt)
      } else {
        setCurrentPlaceholderText(placeholders[currentPlaceholder]?.prompt)
      }
    } else {
      setCurrentPlaceholderText("");

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      } 
    }
  }, [placeholders, currentPlaceholder])

  const {apptheme} = useAppTheme();

  const handleDataFromChild=(data)=> {
    if (contentEditableRef.current) {
      contentEditableRef.current.innerText = data;
      
      // 🔥 Dispatch input event to trigger onInput handler
      const event = new Event("input", { bubbles: true });
      contentEditableRef.current.dispatchEvent(event);
    }
  }

  const sendMessageByVoice = (data) => {
    if (contentEditableRef.current) {
      // Send the message because they said to
      handlePromptSubmit(data);
    }
  }

  return (

    <div className="d-flex justify-center w-100">
      
    <InputContainer
      className={`border border-slate-700 rounded-3 transition-column ${
        isWide ? "col-md-10" : "col-md-10"
      }`}
      style={{
        display: "flex",
        // alignItems: "center",
        flexDirection: "column",
        borderRadius: "50px",
      }}
    >

      <form className="w-100" style={{display: "flex", flexDirection: "row-reverse"}}>
        <div className="d-flex w-100 p-2 position-relative">
          <canvas
            className={cn(
              "ms-3 absolute pointer-events-none text-base transform scale-50 top-[20%] left-2 sm:left-8 origin-top-left filter invert dark:invert-0 pr-20",
              !animating ? "d-none" : "opacity-100"
            )}
            style={{ top: "40%" }}
            ref={canvasRef}
          />

          <EditableTextBox
            role="textbox"
            ref={contentEditableRef}
            contentEditable={acceptsText && !previewFile ? "true" : "false"}
            onInput={(e) => handleTextAreaActions(e)}
            onKeyDown={(e) => handleTextAreaActions(e)}
            aria-multiline
            className={cn(
              "w-full relative text-sm sm:text-base z-50 border-none dark:text-white bg-transparent focus:outline-none focus:ring-0 ps-3 pe-3",
              animating && "text-transparent dark:text-transparent",
              isOverflowing ? "overflow-scroll" : "overflow-hidden"
            )}
            style={{
              resize: "none",
              maxHeight: isOverflowing ? "300px" : "max-content",
              alignSelf: "center",
              color: apptheme.name === "dark-theme" ? "white" : "black",
            }}
          />

          {(
            <button
              disabled={isWaitingForResponse === "waiting"}
              onClick={(e) => triggerFileInput(e)}
              type="submit"
              className="h-8 w-8 rounded-full transition duration-200 flex items-center justify-center"
            >
              <motion.svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="icon icon-tabler icons-tabler-outline icon-tabler-paperclip"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M15 7l-6.5 6.5a1.5 1.5 0 0 0 3 3l6.5 -6.5a3 3 0 0 0 -6 -6l-6.5 6.5a4.5 4.5 0 0 0 9 9l6.5 -6.5" />
              </motion.svg>
            </button>
          )}

          <VoiceChat sendDataToParent={handleDataFromChild} sendMessageByVoice={sendMessageByVoice}/>

          {(
            <button
              onClick={(e) => handleSubmitInput(e)}
              className="h-8 w-8 rounded-full transition duration-200 flex items-center justify-center"
            >
              <motion.svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-gray-300 h-6 w-6"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <motion.path
                  d="M5 12l14 0"
                  initial={{
                    strokeDasharray: "50%",
                    strokeDashoffset: "50%",
                  }}
                  animate={{
                    strokeDashoffset: userPrompt ? "10%" : "50%",
                  }}
                  transition={{
                    duration: 0.3,
                    ease: "linear",
                  }}
                />
                <path d="M13 18l6 -6" />
                <path d="M13 6l6 6" />
              </motion.svg>
            </button>
          )}

          {(
            <input
              id="file-upload"
              type="file"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          )}
          

          {/* {acceptsText && (
            <AnimatePresenceContainer
              className="rounded-full pointer-events-none"
              style={{ bottom: "calc(50% - 12px)" }}
            >
              <AnimatePresence mode="wait">
                {userPrompt.toString().trim() === "" && isWaitingForResponse !== "waiting" && (
                  <motion.p
                    initial={{
                      y: 5,
                      opacity: 0,
                    }}
                    key={`current-placeholder-${currentPlaceholder}`}
                    animate={{
                      y: 0,
                      opacity: 1,
                    }}
                    exit={{
                      y: -15,
                      opacity: 0,
                    }}
                    transition={{
                      duration: 0.3,
                      ease: "linear",
                    }}
                    className="dark:text-zinc-500 text-sm sm:text-base font-normal text-neutral-500 text-left w-100 truncate"
                  >
                    {currentPlaceholderText}
                  </motion.p>
                )}
              </AnimatePresence>
            </AnimatePresenceContainer>
          )} */}
          
        </div>

        {previewFile && (
          <FilePreviewCard
            file={previewFile}
            onRemove={() => setPreviewFile(null)}
          />
        )}
      </form>
     
    </InputContainer>
    
    
    </div>
  );
}
